// frontend/src/components/playground/AuraPlaygroundHUD.jsx
/**
 * Aura Playground HUD - Simulation Overlay
 * Provides visual indicators for "Fake Money Mode" and simulation features
 */

import React from 'react';
import { motion } from 'framer-motion';
import {
  BeakerIcon,
  ExclamationTriangleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const AuraPlaygroundHUD = ({
  children,
  isSimulationMode = true,
  cards = [],
  onCardSelect,
  className = ''
}) => {
  return (
    <div className={`relative min-h-screen ${className}`}>
      {/* Glowing border to signify "Fake Money Mode" */}
      {isSimulationMode && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 pointer-events-none z-40"
        >
          {/* Animated border glow */}
          <motion.div
            className="absolute inset-0 border-4 border-aurora-purple/30"
            style={{
              boxShadow: `
                0 0 20px rgba(139, 92, 246, 0.3),
                0 0 40px rgba(139, 92, 246, 0.2),
                inset 0 0 20px rgba(139, 92, 246, 0.1)
              `
            }}
            animate={{
              boxShadow: [
                '0 0 20px rgba(139, 92, 246, 0.3), 0 0 40px rgba(139, 92, 246, 0.2), inset 0 0 20px rgba(139, 92, 246, 0.1)',
                '0 0 30px rgba(139, 92, 246, 0.4), 0 0 60px rgba(139, 92, 246, 0.3), inset 0 0 30px rgba(139, 92, 246, 0.2)',
                '0 0 20px rgba(139, 92, 246, 0.3), 0 0 40px rgba(139, 92, 246, 0.2), inset 0 0 20px rgba(139, 92, 246, 0.1)'
              ]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </motion.div>
      )}

      {/* Simulation Mode Banner */}
      {isSimulationMode && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center pt-4 pointer-events-none"
        >
          <motion.div
            className="flex items-center gap-3 px-6 py-3 bg-yellow-500/20 backdrop-blur-md border border-yellow-500/40 rounded-full shadow-lg"
            animate={{
              scale: [1, 1.02, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <BeakerIcon className="w-5 h-5 text-yellow-400" />
            <span className="text-yellow-200 font-semibold text-sm">
              🧪 SIMULATION MODE - No Real Money At Risk
            </span>
            <SparklesIcon className="w-4 h-4 text-yellow-400" />
          </motion.div>
        </motion.div>
      )}

      {/* Main Content */}
      <div className="relative z-10">
        {children}
      </div>

      {/* Card Tray at Bottom */}
      {isSimulationMode && cards.length > 0 && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 pointer-events-none"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="bg-glass-card backdrop-blur-xl border border-aurora-purple/30 rounded-2xl p-4 shadow-2xl pointer-events-auto"
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <SparklesIcon className="w-5 h-5 text-aurora-purple" />
                  <h3 className="text-sm font-semibold text-white">Your Action Cards</h3>
                  <span className="text-xs text-gray-400">({cards.length} available)</span>
                </div>
                <div className="text-xs text-gray-400 flex items-center gap-1">
                  <ExclamationTriangleIcon className="w-3 h-3" />
                  <span>Drag cards to Portfolio to simulate</span>
                </div>
              </div>
              
              {/* Scrollable Card Preview Strip */}
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-aurora-purple/30 scrollbar-track-transparent">
                {cards.map((card, index) => (
                  <motion.button
                    key={card.card_id || index}
                    onClick={() => onCardSelect && onCardSelect(card)}
                    disabled={card.played}
                    className={`
                      flex-shrink-0 w-32 h-20 rounded-lg p-3
                      ${card.played 
                        ? 'bg-gray-700/50 opacity-50 cursor-not-allowed' 
                        : 'bg-aurora-purple/20 hover:bg-aurora-purple/30 border border-aurora-purple/30 cursor-pointer'
                      }
                      transition-all
                    `}
                    whileHover={!card.played ? { scale: 1.05 } : {}}
                    whileTap={!card.played ? { scale: 0.95 } : {}}
                  >
                    <div className="flex flex-col items-center justify-center h-full">
                      <span className="text-2xl mb-1">{card.icon || '🎴'}</span>
                      <span className="text-xs font-medium text-white text-center line-clamp-2">
                        {card.title || card.card_type}
                      </span>
                      {card.played && (
                        <span className="text-xs text-gray-400 mt-1">Played</span>
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AuraPlaygroundHUD;
